import { useQuery } from '@tanstack/react-query';
import { Check, Plus, X } from 'lucide-react';
import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';

import type { StorefrontProduct, StorefrontProductSummary } from '@/features/storefront/api/storefront';
import { useQuickCart } from '@/features/storefront/components/QuickCart';
import {
  CARD_SWATCH_MAX,
  cardBadges,
  cardSwatchColour,
  useProductCardSettings,
  type CardBadge,
} from '@/features/storefront/components/productCardSettings';
import { AboveTheFold, StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { useAddToCart } from '@/features/storefront/hooks/useCart';
import type { CardBadgeStyle } from '@/features/storefront/themeSchema';
import { cn } from '@/lib/cn';
import { formatMoney } from '@/lib/money';

/**
 * The pieces the theme's card settings (`ThemeConfig.cards`) add to a product
 * card: a second photo on hover, badges, colour dots and a quick add. Each
 * renders nothing while its setting is off, so `ProductCard` can place them
 * unconditionally and a theme that never touched the settings is unchanged.
 */

/** A click inside a card's own controls must not follow the card's link. */
function stay(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * The product's second photo, laid over the first and faded in on hover -
 * and on keyboard focus, so a shopper tabbing through the grid gets the same
 * preview. Shown when the merchant's `hover_image` feature says so, or, while
 * they have not chosen, when the theme's `cards.hover_image` does. Lazy, so a grid does not download every card's second photo up front - even
 * on a first-row card, which is why it opts out of `AboveTheFold`. It fades
 * itself, and shows no placeholder: an unloaded hover photo must leave the
 * first photo visible, not cover it in the surface colour.
 */
export function CardHoverImage({
  product,
  sizes,
  className,
  style,
}: {
  product: StorefrontProductSummary;
  sizes?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const { cards } = useProductCardSettings();
  const features = useStorefrontFeatures();
  const shown = features.hover_image ?? cards.hover_image;
  if (!shown || !product.hover_image_url) return null;
  return (
    <AboveTheFold value={false}>
      <StorefrontImage
        url={product.hover_image_url}
        sizes={sizes}
        fade={false}
        placeholder={false}
        alt=""
        aria-hidden
        data-card-hover-image
        className={cn(
          'pointer-events-none absolute inset-0 size-full opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100',
          className,
        )}
        style={style}
      />
    </AboveTheFold>
  );
}

const BADGE_COLOURS: Record<CardBadge, CSSProperties> = {
  sale: { backgroundColor: 'var(--storefront-palette-accent)', color: 'var(--storefront-palette-on-accent, #fff)' },
  new: { backgroundColor: 'var(--storefront-palette-primary)', color: 'var(--storefront-palette-background)' },
  bestseller: { backgroundColor: 'var(--storefront-palette-text)', color: 'var(--storefront-palette-background)' },
  sold_out: { backgroundColor: 'var(--storefront-palette-muted)', color: 'var(--storefront-palette-background)' },
};

const BADGE_KEYS: Record<CardBadge, string> = {
  sale: 'product_card.sale',
  new: 'product_card.new',
  bestseller: 'product_card.bestseller',
  sold_out: 'product_card.out_of_stock',
};

const BADGE_SHAPES: Record<CardBadgeStyle, string> = {
  pill: 'rounded-full px-2.5 py-0.5',
  // A strip across the card's corner edge, squared on the side it runs off.
  ribbon: '-ml-3 rounded-r-sm py-1 pl-3 pr-2.5 shadow-sm',
  corner: 'rounded-br-md px-2 py-1',
};

/** The badges the product wears, in the top-left corner of its picture. */
export function CardBadges({ product }: { product: StorefrontProductSummary }) {
  const { cards } = useProductCardSettings();
  const copy = useShopContent();
  const badges = cardBadges(product, cards);
  if (badges.length === 0) return null;
  const style = cards.badge_style;
  return (
    <div
      className={cn(
        'pointer-events-none absolute left-0 top-0 z-10 flex flex-col items-start',
        style === 'corner' ? 'gap-px' : 'gap-1 p-3',
      )}
      data-card-badges={style}
    >
      {badges.map((badge) => (
        <span
          key={badge}
          data-card-badge={badge}
          className={cn('text-[11px] font-semibold uppercase leading-tight tracking-wide', BADGE_SHAPES[style])}
          style={BADGE_COLOURS[badge]}
        >
          {copy(BADGE_KEYS[badge])}
        </span>
      ))}
    </div>
  );
}

/**
 * The product's colours as dots - at most five, then "+n". A colour no list
 * can paint is a small text chip with its name, which is honest about what it
 * is rather than a guessed dot.
 */
export function CardSwatches({ product, className }: { product: StorefrontProductSummary; className?: string }) {
  const { cards } = useProductCardSettings();
  const colours = product.colours ?? [];
  if (!cards.swatches || colours.length === 0) return null;
  const shown = colours.slice(0, CARD_SWATCH_MAX);
  const more = Math.max(0, (product.colour_count ?? colours.length) - shown.length);
  return (
    <ul className={cn('mt-2 flex flex-wrap items-center gap-1.5', className)} data-card-swatches>
      {shown.map((name) => {
        const colour = cardSwatchColour(name);
        return colour ? (
          <li
            key={name}
            title={name}
            aria-label={name}
            className="size-3.5 rounded-full border"
            style={{ backgroundColor: colour, borderColor: 'color-mix(in srgb, currentColor 25%, transparent)' }}
          />
        ) : (
          <li
            key={name}
            className="rounded-full border px-1.5 text-[10px] leading-4"
            style={{ borderColor: 'color-mix(in srgb, currentColor 25%, transparent)' }}
          >
            {name}
          </li>
        );
      })}
      {more > 0 ? (
        <li className="text-[11px]" data-card-swatch-more>
          +{more}
        </li>
      ) : null}
    </ul>
  );
}

/** "Red / M" - a variant as its option values, for the picker. */
function variantLabel(variant: StorefrontProduct['variants'][number]): string {
  const values = Object.values(variant.options ?? {})
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);
  return values.length > 0 ? values.join(' / ') : variant.sku || '-';
}

/**
 * The card's own add to cart. Nothing while the theme has it off, or while
 * the product is sold out.
 *
 * A product with one variant goes straight into the basket; one with several
 * opens a small picker over the picture, loaded from the product itself only
 * when it opens. Afterwards the quick cart slides in when its plugin says to
 * open on add, and the button says "Added" for a moment otherwise.
 */
export function CardQuickAdd({
  product,
  placement = 'bottom',
}: {
  product: StorefrontProductSummary;
  /** Where the control sits on the picture: the overlay card's caption owns the bottom. */
  placement?: 'bottom' | 'top';
}) {
  const { cards, inert } = useProductCardSettings();
  if (cards.quick_add === 'off' || !product.in_stock) return null;
  return <QuickAddControl product={product} mode={cards.quick_add} inert={inert} placement={placement} />;
}

function QuickAddControl({
  product,
  mode,
  inert,
  placement,
}: {
  product: StorefrontProductSummary;
  mode: 'button' | 'icon';
  inert: boolean;
  placement: 'bottom' | 'top';
}) {
  const copy = useShopContent();
  const add = useAddToCart();
  const quickCart = useQuickCart();
  const [picking, setPicking] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!added) return;
    const timer = window.setTimeout(() => setAdded(false), 2000);
    return () => window.clearTimeout(timer);
  }, [added]);

  const addVariant = (variantId: string) =>
    add.mutate(
      { variantId },
      {
        onSuccess: () => {
          setPicking(false);
          if (quickCart.openOnAdd) quickCart.openCart();
          else setAdded(true);
        },
      },
    );

  const single = (product.variant_count ?? 0) <= 1 && Boolean(product.default_variant_id);
  const onClick = (event: MouseEvent) => {
    stay(event);
    if (inert || add.isPending) return;
    if (single && product.default_variant_id) addVariant(product.default_variant_id);
    else setPicking(true);
  };

  const label = added ? copy('product_card.added') : copy('product_card.quick_add');
  return (
    <>
      {mode === 'icon' ? (
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          title={label}
          data-card-quick-add="icon"
          className={cn(
            'absolute right-3 z-10 grid size-9 place-items-center rounded-full shadow-md transition-transform hover:scale-105',
            placement === 'top' ? 'top-3' : 'bottom-3',
          )}
          style={{ backgroundColor: 'var(--storefront-palette-background)', color: 'var(--storefront-palette-text)' }}
        >
          {added ? <Check className="size-4" aria-hidden /> : <Plus className="size-4" aria-hidden />}
        </button>
      ) : (
        <button
          type="button"
          onClick={onClick}
          data-card-quick-add="button"
          className={cn(
            'absolute inset-x-3 z-10 py-2 text-xs font-semibold uppercase tracking-wide transition-opacity',
            'focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100',
            placement === 'top' ? 'top-3' : 'bottom-3',
          )}
          style={{
            backgroundColor: 'var(--storefront-button-bg)',
            color: 'var(--storefront-button-text)',
            border: '1px solid var(--storefront-palette-primary)',
            borderRadius: 'var(--storefront-radius)',
          }}
        >
          {label}
        </button>
      )}
      {picking ? (
        <VariantPicker product={product} pending={add.isPending} onPick={addVariant} onClose={() => setPicking(false)} />
      ) : null}
    </>
  );
}

function VariantPicker({
  product,
  pending,
  onPick,
  onClose,
}: {
  product: StorefrontProductSummary;
  pending: boolean;
  onPick: (variantId: string) => void;
  onClose: () => void;
}) {
  const copy = useShopContent();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  // The product page's own query, so opening the picker warms the page and a
  // shopper who already saw the product gets it from the cache.
  const detail = useQuery({
    queryKey: [...scope, 'product', product.slug],
    queryFn: () => source.product(product.slug),
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  let body: ReactNode;
  if (detail.isPending) body = <p className="text-xs">{copy('common.loading')}</p>;
  else if (!detail.data) body = null;
  else
    body = (
      <ul className="max-h-40 space-y-1 overflow-y-auto">
        {detail.data.variants.map((variant) => (
          <li key={variant.id}>
            <button
              type="button"
              disabled={!variant.in_stock || pending}
              onClick={(event) => {
                stay(event);
                onPick(variant.id);
              }}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>{variantLabel(variant)}</span>
              <span>{variant.in_stock ? formatMoney(variant.price) : copy('product_card.out_of_stock')}</span>
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    // The whole popover swallows clicks: it sits inside the card's link.
    <div
      role="dialog"
      aria-label={copy('product_card.choose_options')}
      onClick={stay}
      className="absolute inset-x-2 bottom-2 z-20 rounded-md p-3 shadow-lg"
      style={{ backgroundColor: 'var(--storefront-palette-background)', color: 'var(--storefront-palette-text)' }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold">{copy('product_card.choose_options')}</p>
        <button
          type="button"
          aria-label={copy('product_card.close')}
          onClick={(event) => {
            stay(event);
            onClose();
          }}
          className="grid size-6 place-items-center rounded-full hover:bg-black/5"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
      {body}
    </div>
  );
}
